import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import uuid from "react-native-uuid";
import { Square, View, XStack, YStack } from "tamagui";
import { useUploadMedia } from "../../../core/hooks/useUploadMedia";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { ImageFit } from "../../../ui/ImageFit";
import { Input } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { EditorForm } from "../../../ui/YooptaEditor";
import type { BusinessCustomerModel } from "../customers/BusinessCustomerModel";
import { BusinessAdminProfileQuestions } from "./BusinessAdminProfileQuestions";
import { BusinessInfoRow } from "./BusinessInfoRow";

export const BusinessAdminSettings = observer(
  ({ customer }: { customer: BusinessCustomerModel }) => {
    const getStoragePathSuffix = React.useCallback(async () => {
      return `${customer.uid}/${uuid.v4()}`;
    }, [customer.uid]);

    const backgroundColor =
      customer.data.profile.theme?.pressablePrimaryBackground ?? "transparent";

    const { isUploading, imageSrc, selectFile, uploadFile } =
      useUploadMedia(getStoragePathSuffix);

    const customerLogo = imageSrc || customer.data.profile.logoUrl;

    const selectAndUpload = useCallback(async () => {
      const result = await selectFile();

      if (result) {
        const url = await uploadFile(result.compressedFile);
        customer.updateLogo(url);
      }
    }, [selectFile, uploadFile, customer]);

    return (
      <YStack gap={"$medium"}>
        <Box gap="$large">
          <BusinessInfoRow title={"Update your colors and logotype."} />

          <Label>Logo</Label>
          <XStack alignItems="center" gap={"$medium"}>
            <View
              borderColor="$divider"
              borderWidth={1}
              height={56}
              width={200}
              alignItems="center"
              justifyContent="center"
            >
              {customerLogo && (
                <ImageFit assetUrl={customerLogo} width={"100%"} height={50} />
              )}
            </View>
            <Button
              isLoading={isUploading}
              flex={1}
              type="secondary"
              onPress={selectAndUpload}
            >
              Upload
            </Button>
          </XStack>

          <Label>Colors</Label>

          <XStack gap="$small">
            <Square
              width={"$large"}
              aspectRatio={1}
              style={{
                backgroundColor:
                  customer.data.profile.theme?.pressablePrimaryLabel ??
                  "transparent",
              }}
            />
            <Input
              placeholder="Label Color #XXXXXX"
              type="border"
              value={customer.data.profile.theme?.pressablePrimaryLabel ?? ""}
              submitOnBlur
              onSubmitEditing={(event) => {
                customer.updateTheme({
                  pressablePrimaryLabel: event.nativeEvent.text,
                });
              }}
              submitOnEnter
            />
          </XStack>
          <XStack gap="$small">
            <Square
              width={"$large"}
              aspectRatio={1}
              style={{ backgroundColor }}
            />
            <Input
              placeholder="Background Color #XXXXXX"
              type="border"
              value={
                customer.data.profile.theme?.pressablePrimaryBackground ?? ""
              }
              submitOnBlur
              onSubmitEditing={(event) => {
                customer.updateTheme({
                  pressablePrimaryBackground: event.nativeEvent.text,
                });
              }}
              submitOnEnter
            />
          </XStack>
        </Box>

        <BusinessAdminProfileQuestions customer={customer} />

        <Box gap="$large">
          <BusinessInfoRow title={"Add a custom intro/welcome text."} />

          <Body>
            You can use <Body fontWeight={"bold"}>@displayName@</Body> and{" "}
            <Body fontWeight={"bold"}>@eventName@</Body> as replacements in the
            text.
          </Body>
          <EditorForm
            id="introText"
            value={customer.data.profile.introText}
            onSave={(data) => customer.updateIntroText(data)}
          />
        </Box>
      </YStack>
    );
  },
);
